import theme from '../../../common/theme';
import { themeType } from '../../../common/theme-colors';
import { useTheme } from '../../../hooks/useTheme';
import React, { useMemo } from 'react';
import { View, TextInput, Text, ViewStyle, StyleSheet } from 'react-native';
import {
  Control,
  Controller,
  FieldError,
  FieldPath,
  FieldValues,
} from 'react-hook-form';

export interface FormInputProps<T extends FieldValues> {
  control: Control<T>;
  readOnly?: boolean;
  name: FieldPath<T>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  rules?: any;
  placeholder?: string;
  containerStyle?: ViewStyle | ViewStyle[];
}

const makeStyles = (colors: themeType) =>
  StyleSheet.create({
    formInputContainer: {
      display: 'flex',
      width: '100%',
    },
    input: {
      display: 'flex',
      borderWidth: 1,
      borderRadius: 6,
      borderColor: colors.borderColor,
      backgroundColor: colors.elevation1,
      height: 44,
    },
    inputContainer: {
      height: 44,
      borderRadius: 6,
      paddingRight: 12,
      paddingLeft: 12,
      display: 'flex',
      flex: 1,
      color: colors.text,
    },
    errorText: {
      fontSize: theme.fontSize.small,
      paddingTop: 6,
      paddingLeft: 12,
      color: theme.colors.states.negative,
      textAlign: 'left',
    },
  });

const FormInput = <T extends FieldValues>({
  control,
  readOnly = false,
  rules,
  name,
  placeholder,
  containerStyle,
}: FormInputProps<T>) => {
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({
        field: { onChange, onBlur, value },
        formState: { isDirty, errors },
      }) => (
        <View style={[styles.formInputContainer, containerStyle]}>
          <View style={styles.input}>
            <TextInput
              onBlur={onBlur}
              onChangeText={onChange}
              value={value as string}
              placeholderTextColor={colors.textLight}
              placeholder={placeholder}
              editable={!readOnly}
              style={styles.inputContainer}
            />
          </View>
          {isDirty && (errors[name] as FieldError)?.message && (
            <Text style={styles.errorText} testID="errorMessage">
              {(errors[name] as FieldError)?.message}
            </Text>
          )}
        </View>
      )}
    />
  );
};

export default FormInput;
